import React, { useEffect, useRef } from 'react';
import { translate, translateFormat } from '../Services/translation';
import { dataLayerRemoveFromCart } from '../Services/Gtm.service';

const PANEL_ID = 'minicart-panel';
const TITLE_ID = 'minicart-title';
const FOCUSABLE = 'a[href], button:not([disabled]):not([tabindex="-1"])';

const text = (key, fallback) => window.__litium.translation[key] || fallback;

const getProduct = (row, updateOrderRowQuantity, removeOrderRow) => {
    // Products sold in packs (2-pack, 4-pack, ...) step a whole pack at a time.
    const packSize = Math.max(1, row.packSize || 1);
    const hasMax = row.maxPurchaseQuantity !== null && row.maxPurchaseQuantity !== undefined;
    const atMax = hasMax && row.quantity + packSize > row.maxPurchaseQuantity;

    return <div className="item-info" data-article-number={row.articleNumber} key={row.rowSystemId}>
        <div className="image"><img src={row.image} alt="" /></div>
        <div className="info">
            <h3>{row.name}</h3>
            <span>{row.deliveryStatusText}</span>
            {packSize > 1 && <span className="item-info__pack-size">{translateFormat('product.soldinpack', packSize)}</span>}
            {row.totalCampaignPrice ? (
              <>
                <div className="original-price">{row.totalPrice}</div>
                <div className="price">{row.totalCampaignPrice}</div>
              </>
            ) : (
              <div className="price">{row.totalPrice}</div>
            )}
            <div className="edit">
                <div className="plus-minus">
                    <button type="button" className="quantity minus" aria-label={`${text('minicart.decrease', 'Minska antal')}, ${row.name}`} onClick={() => updateOrderRowQuantity(row.rowSystemId, Math.max(0, row.quantity - packSize))}>-</button>
                    <div className="total" aria-live="polite">{row.quantityString}</div>
                    <button type="button" className="quantity plus" aria-label={`${text('minicart.increase', 'Öka antal')}, ${row.name}`} disabled={atMax} onClick={() => !atMax && updateOrderRowQuantity(row.rowSystemId, row.quantity + packSize)}>+</button>
                </div>
                <div className="delete">
                    <button type="button" className="cart__remove-link" aria-label={`${translate('minicart.remove')}, ${row.name}`} onClick={() => removeOrderRow(row.rowSystemId)}>{translate('minicart.remove')}</button> {/* Ta bort */}

                    <button type="button" className="cart__product-row__remove-button" tabIndex={-1} aria-hidden="true" onClick={() => removeOrderRow(row.rowSystemId)}></button>
                </div>
            </div>
        </div>
    </div>
}

const MiniCart = ({ quantity, grandTotal, checkoutUrl, showInfo, toggle, updateOrderRowQuantity, removeOrderRow, orderRows, notifications, dismissNotification }) => {
    const iconRef = useRef(null);
    const panelRef = useRef(null);
    const wasOpen = useRef(showInfo);

    useEffect(() => {
        const panel = panelRef.current;
        document.documentElement.classList.toggle('cart-scroll-lock', !!showInfo);
        if (showInfo && !wasOpen.current) {
            panel.focus({ preventScroll: true });
        } else if (!showInfo && wasOpen.current) {
            // Only take focus back from inside the cart; an outside click has already put focus where the user wants it.
            const active = document.activeElement;
            if (!active || active === document.body || panel.contains(active)) {
                iconRef.current.focus({ preventScroll: true });
            }
        }
        wasOpen.current = showInfo;
    }, [showInfo]);

    useEffect(() => {
        // Removing a row also removes the button that had focus.
        if (showInfo && (!document.activeElement || document.activeElement === document.body)) {
            panelRef.current.focus({ preventScroll: true });
        }
    }, [orderRows]);

    const onKeyDown = (e) => {
        if (!showInfo) {
            return;
        }
        if (e.key === 'Escape') {
            toggle();
            return;
        }
        if (e.key !== 'Tab') {
            return;
        }
        const focusables = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
        if (!focusables.length) {
            return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || !panelRef.current.contains(active) || active === panelRef.current)) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && (active === last || !panelRef.current.contains(active))) {
            e.preventDefault();
            first.focus();
        }
    };

    return (
        <div className="cart cart--mini" onKeyDown={onKeyDown}>
            <button
                type="button"
                className="icon"
                ref={iconRef}
                onClick={() => toggle()}
                aria-expanded={!!showInfo}
                aria-controls={PANEL_ID}
                aria-label={`${translate('minicart.cart')}, ${quantity}`}
            >
                <img src="/ui/images/shopping_bag-24px.svg" alt="" className="b2c" />
                <img src="/ui/images/shopping_bag-24px_w.svg" alt="" className="b2b" />
            </button>
            <div className={`bullet ${parseFloat(quantity) > 0 ? '' : 'bullet--empty'}`} aria-hidden="true" onClick={() => toggle()}>{quantity}</div>
            <div
                id={PANEL_ID}
                className={`cart__info ${!showInfo ? 'cart__info--hidden' : ''}`}
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={TITLE_ID}
                tabIndex={-1}
            >
                <div className="varukorg" id={TITLE_ID}>
                    {translate('minicart.cart')} {/* Varukorg */}
                </div>
                <button type="button" className="cart__close-button" aria-label={text('minicart.close', 'Stäng')} onClick={() => toggle()}>
                </button>
                <div>
                    <br />
                    {notifications && notifications.length > 0 && (
                        <div className="cart__notifications" role="status">
                            {notifications.map((n, i) => (
                                <div key={i} className={`cart__notification cart__notification--${n.type || 'info'}`}>
                                    <span className="cart__notification-text">{n.text}</span>
                                    <button
                                        type="button"
                                        className="cart__notification-close"
                                        onClick={() => dismissNotification(i)}
                                        aria-label={translate('minicart.notification.dismiss')}
                                    >×</button>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="cart__product-row">
                        {orderRows.map(row => {
                            const removeOrderRowWithTracking = (rowId) => {
                                try {
                                    dataLayerRemoveFromCart([row.gtmEcommerceItem]);
                                } catch (e) {
                                    // Tracking must never stop the row from being removed.
                                }
                                removeOrderRow(rowId);
                            }

                            return getProduct(row, updateOrderRowQuantity, removeOrderRowWithTracking);
                        })}
                    </div>
                </div>
                <div className="cart__summary">
                    <div className="total-sum">
                        {translate('minicart.sum')} {grandTotal} {/* Summa: */}
                    </div>
                </div>
                <a href={checkoutUrl} className="cart__checkout-button">{translate('minicart.checkout')}</a>
                <button type="button" className="cart__checkout-button black" onClick={() => toggle()}>{translate('minicart.continueshopping')}</button> {/* Fortsätt handla */}

            </div>
            <div className={`cart__overlay ${showInfo ? 'cart__overlay--visible' : ''}`} aria-hidden="true" onClick={() => toggle()}></div>
        </div>
    );
}

export default MiniCart;
